import { ViewUrlType } from 'types';

export interface ViewUrl {
  url: string;
  type: ViewUrlType;
  expiresAt?: string;
}

const DEFAULT_FALLBACK_ORDER: ViewUrlType[] = [
  ViewUrlType.original,
  ViewUrlType.preview,
  ViewUrlType.gallery,
  ViewUrlType.thumbnail,
];

export const getViewUrl = (viewUrls: ViewUrl[] | undefined, preferredType: ViewUrlType): string | undefined => {
  if (!viewUrls?.length) return undefined;
  const preferred = viewUrls.find((v) => v.type === preferredType);
  if (preferred) return preferred.url;

  for (const type of DEFAULT_FALLBACK_ORDER) {
    const url = viewUrls.find((v) => v.type === type);
    if (url) return url.url;
  }
  return viewUrls[0]?.url;
};

export const getViewUrlObject = (
  viewUrls: ViewUrl[] | undefined,
  preferredType: ViewUrlType,
): ViewUrl | undefined => {
  if (!viewUrls?.length) return undefined;
  const preferred = viewUrls.find((v) => v.type === preferredType);
  if (preferred) return preferred;

  for (const type of DEFAULT_FALLBACK_ORDER) {
    const url = viewUrls.find((v) => v.type === type);
    if (url) return url;
  }
  return viewUrls[0];
};

/**
 * Whether the URL has expired or will expire within the buffer window.
 * Buffer defaults to 5 minutes.
 */
export const isUrlExpired = (expiresAt: string | undefined, bufferMs = 5 * 60 * 1000): boolean => {
  if (!expiresAt) return false;
  const expirationTime = new Date(expiresAt).getTime();
  const currentTime = Date.now();
  return currentTime >= expirationTime - bufferMs;
};

export const getThumbnailUrl = (viewUrls: ViewUrl[] | undefined): string | undefined =>
  getViewUrl(viewUrls, ViewUrlType.thumbnail);

/** Returns the thumbnail URL only if a thumbnail variant actually exists — no fallback. */
export const getStrictThumbnailUrl = (viewUrls: ViewUrl[] | undefined): string | undefined =>
  viewUrls?.find((v) => v.type === ViewUrlType.thumbnail)?.url;

export const getPreviewUrl = (viewUrls: ViewUrl[] | undefined): string | undefined => {
  if (!viewUrls?.length) return undefined;
  const previewFallbackOrder: ViewUrlType[] = [
    ViewUrlType.preview,
    ViewUrlType.original,
    ViewUrlType.gallery,
    ViewUrlType.thumbnail,
  ];
  for (const type of previewFallbackOrder) {
    const url = viewUrls.find((v) => v.type === type);
    if (url) return url.url;
  }
  return viewUrls[0]?.url;
};

export const getOriginalUrl = (viewUrls: ViewUrl[] | undefined): string | undefined => {
  if (!viewUrls?.length) return undefined;
  const original = viewUrls.find((v) => v.type === ViewUrlType.original);
  return original?.url ?? viewUrls[0]?.url;
};
